/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Page SubscriberId AdditionalAccounts Tab component
 *
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @version 1.14.1
 * @since 1.12.0
 */

import { Fragment } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Button, Skeleton } from '@components/common';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { wizardStorageClearStepsAction } from '@modules/wizard/wizardActions';
import { EWizardName } from '@modules/wizard/wizardConstants';
import { useSubscriberIdContext } from '@pages/PageSubscriberId/context';
import { ESubscriberIdActions } from '@pages/PageSubscriberId/types';
import { EAdditionalAccountType } from '@pages/WizardAdditionalAccounts/types/wizardAdditionalAccountsTypes';
import { openRoute } from '@routes/constants/externalRoutes';
import { RoutePath, SUBSCRIBER_ID } from '@routes/constants/routesConstants';
import { formatBalance, formatDate } from '@utils/formatText';

import SubscriberIdError from '../../SubscriberIdError';
import { additionalAccountsTranslationSelector } from '../selectors';
import { useGetAccountsQuery } from '../store';
import { IAdditionalAccountDescription, IAdditionalAccountInfo } from '../types';
import { SubscriberIdAdditionalAccountsStyled } from './styled/SubscriberIdAdditionalAccountsStyled';
import SubscriberIdAdditionalAccountsSkeleton from './SubscribersIdAdditionalAccountsSkeleton';

const SubscriberIdAdditionalAccounts = () => {
    const { subscriberId } = useSubscriberIdContext();

    const dispatch = useDispatch();

    const { data, isFetching, isSuccess, error, refetch } = useGetAccountsQuery(subscriberId);

    const tabTranslation = useSelector(additionalAccountsTranslationSelector());
    const { currency } = useSelector(makeSelectTranslationCommon());

    const connectAdditionalAccounts = (accountType: EAdditionalAccountType) => {
        // Clear wizard data when click on connect/disconnect additional accounts
        dispatch(
            wizardStorageClearStepsAction({
                wizardName: EWizardName.ADDITIONAL_ACCOUNTS,
            }),
        );

        // start master
        const url = RoutePath.wizardAdditionalAccountsFromSubscriberId.replace(SUBSCRIBER_ID, subscriberId);
        openRoute(`${url}#${accountType}`);
    };

    const onBthPersonalAccountManageClick = () => {
        connectAdditionalAccounts(EAdditionalAccountType.PERSONAL);
    };

    const onBthContentAccountManageClick = () => {
        connectAdditionalAccounts(EAdditionalAccountType.CONTENT);
    };

    if (error || (isSuccess && !data)) return <SubscriberIdError refetch={refetch} />;
    if (isFetching) return <Skeleton isLoading render={SubscriberIdAdditionalAccountsSkeleton} />;
    if (!data) return null;

    const renderAdditionalAccount = (
        isPersonalAccount: boolean,
        description?: IAdditionalAccountDescription,
        info?: IAdditionalAccountInfo,
    ) =>
        info ? (
            <Fragment>
                <div className="subscriber-id-additional-accounts__content">
                    <div className="subscriber-id-additional-accounts__subtitle">{tabTranslation.number}</div>
                    <div className="subscriber-id-additional-accounts__text">{info.accountNumber}</div>
                    <div className="subscriber-id-additional-accounts__subtitle">{tabTranslation.status}</div>
                    <div className="subscriber-id-additional-accounts__text">{info.status}</div>
                    <div className="subscriber-id-additional-accounts__subtitle">{tabTranslation.date}</div>
                    <div className="subscriber-id-additional-accounts__text">{formatDate(info.startDate)}</div>
                    <div className="subscriber-id-additional-accounts__subtitle">{tabTranslation.balance}</div>
                    <div className="subscriber-id-additional-accounts__text">
                        {formatBalance(info.balance)}&nbsp;{currency}
                    </div>
                </div>
                {data.actions.includes(ESubscriberIdActions.accountChange) && (
                    <div>
                        <ArrowTooltip
                            disableHoverListener={!isPersonalAccount}
                            title={tabTranslation.personalAccountDisconnectionTooltip}
                            titleAlign="center"
                        >
                            <span>
                                <Button
                                    onClick={
                                        isPersonalAccount
                                            ? onBthPersonalAccountManageClick
                                            : onBthContentAccountManageClick
                                    }
                                >
                                    {tabTranslation.btnDisconnection}
                                </Button>
                            </span>
                        </ArrowTooltip>
                    </div>
                )}
            </Fragment>
        ) : (
            <Fragment>
                <div className="subscriber-id-additional-accounts__content">
                    <div className="subscriber-id-additional-accounts__text">{tabTranslation.info}</div>
                    <div>{description?.text}</div>
                </div>
                {data.actions.includes(ESubscriberIdActions.accountChange) && (
                    <div>
                        <ArrowTooltip
                            disableHoverListener={isPersonalAccount}
                            title={tabTranslation.contentAccountConnectionTooltip}
                            titleAlign="center"
                        >
                            <span>
                                <Button
                                    onClick={
                                        isPersonalAccount
                                            ? onBthPersonalAccountManageClick
                                            : onBthContentAccountManageClick
                                    }
                                >
                                    {tabTranslation.btnConnection}
                                </Button>
                            </span>
                        </ArrowTooltip>
                    </div>
                )}
            </Fragment>
        );

    return (
        <SubscriberIdAdditionalAccountsStyled>
            <div className="subscriber-id-additional-accounts__block">
                <div className="subscriber-id-additional-accounts__title">{tabTranslation.personalAccountTitle}</div>
                {renderAdditionalAccount(true, data.personalAccountDescription, data.personalAccount)}
            </div>
            <div className="subscriber-id-additional-accounts__block">
                <div className="subscriber-id-additional-accounts__title">{tabTranslation.contentAccountTitle}</div>
                {renderAdditionalAccount(false, data.contentAccountDescription, data.contentAccount)}
            </div>
        </SubscriberIdAdditionalAccountsStyled>
    );
};

export default SubscriberIdAdditionalAccounts;
